import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import {
  FocusMemory,
  focusKey,
  isDirectionalInput,
  notePointerInput,
  selectorForKey,
} from './focus-memory';

/**
 * Powrót na ekran wraca na ostatni zaznaczony element.
 *
 * Pamięć żyje **poza komponentem**, bo `AppShell` montuje się raz, ale i tak: to stan
 * karty, nie stan renderu. Nic tu nie wywołuje przerysowania.
 */
const memory = new FocusMemory();

/**
 * Jak długo szukamy elementu po wejściu na ekran.
 *
 * Pasy kafli dochodzą z zapytań, więc w pierwszej klatce po powrocie zwykle nie ma
 * jeszcze czego zaznaczyć. Dwie sekundy pokrywają odpowiedź z pamięci podręcznej
 * i zapytanie do serwera; po tym czasie kafel prawdopodobnie już nie istnieje —
 * i wtedy nie robimy nic, zamiast zaznaczać coś innego w tym samym miejscu.
 */
const RESTORE_WINDOW_MS = 2000;

/** Odstęp między próbami — na tyle krótki, by przywrócenie nie było widoczne. */
const RETRY_MS = 120;

export function useFocusMemory(): void {
  const { pathname } = useLocation();

  /**
   * Ścieżka w refie, nie w domknięciu nasłuchu.
   *
   * Fokus zapamiętujemy dla ekranu, na którym stał, a nasłuch zakładamy raz. Bez refa
   * domknięcie trzymałoby ścieżkę z chwili montażu i cała pamięć osiadałaby pod
   * adresem, z którego widz wystartował.
   */
  const pathRef = useRef(pathname);
  pathRef.current = pathname;

  useEffect(() => {
    function onFocusIn(event: FocusEvent): void {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      const key = focusKey({
        focusKey: target.dataset.focusKey ?? null,
        href: target.getAttribute('href'),
        id: target.getAttribute('id'),
      });
      if (key !== null) memory.remember(pathRef.current, key);
    }

    // `focusin`, nie `focus`: to pierwsze bąbelkuje, więc jeden nasłuch na dokumencie
    // obejmuje każdy element — także te, których jeszcze nie ma.
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('pointerdown', notePointerInput);
    return () => {
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('pointerdown', notePointerInput);
    };
  }, []);

  useEffect(() => {
    if (!isDirectionalInput()) return;
    // Odtwarzacz ma własne znaczenie strzałek i własną warstwę sterowania —
    // wskakiwanie fokusem w jego przyciski po wejściu byłoby zaczepieniem OSD.
    if (document.documentElement.dataset.immersive === 'true') return;

    const key = memory.recall(pathname);
    if (key === null) return;
    const maybeSelector = selectorForKey(key);
    if (maybeSelector === null) return;
    // Kopia po zawężeniu: `attempt` jest deklaracją funkcji, czyli jest wynoszona
    // na początek zakresu, więc w jej wnętrzu typ `maybeSelector` jest wciąż
    // niezawężony. Ten `const` nosi już samo `string`.
    const selector = maybeSelector;

    let cancelled = false;
    let timer: number | undefined;

    /**
     * Cokolwiek zrobi widz, przestajemy przywracać.
     *
     * Przywrócenie ma być pomocą, a nie przejęciem sterowania: jeśli w tej sekundzie
     * nacisnął już strzałkę albo kliknął, fokus należy do jego decyzji.
     */
    function cancel(): void {
      cancelled = true;
    }

    const deadline = Date.now() + RESTORE_WINDOW_MS;

    function attempt(): void {
      if (cancelled) return;
      const target = document.querySelector(selector);
      // Element bez obszaru jest jeszcze zastępczy albo schowany — zaznaczenie
      // takiego przewinęłoby stronę w miejsce, w którym nic nie widać.
      if (target instanceof HTMLElement && target.getBoundingClientRect().height > 0) {
        target.focus();
        target.scrollIntoView({ block: 'center', inline: 'center' });
        return;
      }
      if (Date.now() < deadline) timer = window.setTimeout(attempt, RETRY_MS);
    }

    window.addEventListener('keydown', cancel, { once: true });
    window.addEventListener('pointerdown', cancel, { once: true });
    attempt();

    return () => {
      cancelled = true;
      if (timer !== undefined) window.clearTimeout(timer);
      window.removeEventListener('keydown', cancel);
      window.removeEventListener('pointerdown', cancel);
    };
  }, [pathname]);
}
